import React, { useState, useEffect } from 'react';
import { Grid, Box, Typography, Card, CardContent, CircularProgress, Alert } from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import config from '../config';

function MetricCard({ title, value, trend, trendValue, trendLabel }) {
  const isPositive = trend === 'up';
  const TrendIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 600,
            fontSize: '0.7rem',
            display: 'block',
            mb: 1.5
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              fontSize: '2.25rem',
              lineHeight: 1
            }}
          >
            {value}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendIcon sx={{ fontSize: 16, color: isPositive ? '#10b981' : '#ef4444' }} />
            <Typography
              variant="caption"
              sx={{
                color: isPositive ? '#10b981' : '#ef4444',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {trendValue}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontSize: '0.75rem'
          }}
        >
          {trendLabel || 'vs previous period'}
        </Typography>
      </CardContent>
    </Card>
  );
}

// Smart number formatter that chooses K or M based on value size
const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const colorMap = {
      'Charges': '#2563eb',
      'Payments': '#10b981',
      'Payment Ratio': '#f59e0b',
      'Revenue': '#10b981',
      'Adjustments': '#f97316'
    };

    // Check if adjustments data exists in the payload's data point
    const dataPoint = payload[0]?.payload;
    const adjustments = dataPoint?.adjustments;

    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          p: 1.5,
          fontSize: '0.875rem'
        }}
      >
        <Typography sx={{ color: '#64748b', fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorMap[entry.name] || entry.color }} />
            <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.875rem' }}>
              {entry.name}: <strong style={{ color: '#1e293b' }}>
                {entry.name === 'Payment Ratio' ? `${entry.value}%` : formatCurrency(entry.value)}
              </strong>
            </Typography>
          </Box>
        ))}
        {adjustments !== undefined && adjustments > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, mt: 0.5, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f97316' }} />
            <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.875rem' }}>
              Adjustments: <strong style={{ color: '#1e293b' }}>
                {formatCurrency(adjustments)}
              </strong>
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
  return null;
};

function ExecutiveOverview({ filters, consolidateLocations = true }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const queryParams = new URLSearchParams({
          dateType: filters.dateType || 'post',
          consolidateLocations: consolidateLocations.toString()
        });
        const response = await fetch(`${config.apiBaseUrl}/api/executive-overview?${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        console.error('Error fetching executive overview data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters.dateType, consolidateLocations]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load dashboard data: {error}
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No data available
        </Alert>
      </Box>
    );
  }

  // Extract data from API response
  const { mtd, ytd, monthlyTrend, revenueByPayor, revenueByLocation } = data;

  return (
    <Box>
      {/* Compact Metrics - MTD & YTD in Single Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {/* MTD Section */}
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e2e8f0' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 700,
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5
              }}
            >
              Month to Date
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Charges
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                    {formatCurrency(mtd.charges)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Adjustments
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f97316', fontSize: '1.1rem' }}>
                    {formatCurrency(mtd.adjustments)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Payments
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981', fontSize: '1.1rem' }}>
                    {formatCurrency(mtd.collections)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Gross Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '1.1rem' }}>
                    {mtd.collectionRate}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Patients
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '1.1rem' }}>
                    {mtd.patientVolume?.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* YTD Section */}
        <Grid item xs={12} md={6}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, border: '1px solid #e2e8f0' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 700,
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5
              }}
            >
              Year to Date
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Charges
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                    {formatCurrency(ytd.charges)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Adjustments
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f97316', fontSize: '1.1rem' }}>
                    {formatCurrency(ytd.adjustments)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Payments
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981', fontSize: '1.1rem' }}>
                    {formatCurrency(ytd.collections)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Gross Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '1.1rem' }}>
                    {ytd.collectionRate}%
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2.4}>
                <Box sx={{ bgcolor: '#f8fafc', borderRadius: 2, p: 1.5, height: '100%', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Patients
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '1.1rem' }}>
                    {ytd.patientVolume?.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* Charts - Full Width Trend + 2 Column Grid for Revenue Breakdowns */}
      <Grid container spacing={3}>
        {/* Monthly Charges & Collections Trend - Full Width */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '1rem'
                }}
              >
                Monthly Charges & Payments Trend
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="month"
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCurrency}
                  />
                  {/* Right YAxis for Payment Ratio - Commented out per customer request */}
                  {/* <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#f59e0b"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `${value}%`}
                    domain={[75, 88]}
                  /> */}
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    yAxisId="left"
                    dataKey="billed"
                    fill="#dbeafe"
                    stroke="#2563eb"
                    strokeWidth={1}
                    name="Charges"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="collected"
                    fill="#d1fae5"
                    stroke="#10b981"
                    strokeWidth={1}
                    name="Payments"
                    radius={[4, 4, 0, 0]}
                  />
                  {/* Payment Ratio Line - Commented out per customer request */}
                  {/* <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="collectionRate"
                    stroke="#f59e0b"
                    strokeWidth={3}
                    dot={{ fill: '#f59e0b', r: 3 }}
                    name="Payment Ratio"
                  /> */}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue by Payor */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '1rem'
                }}
              >
                Charges & Payments by Payor
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueByPayor} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCurrency}
                  />
                  <YAxis
                    dataKey="payor"
                    type="category"
                    stroke="#94a3b8"
                    width={120}
                    style={{ fontSize: '0.65rem' }}
                    axisLine={false}
                    tickLine={false}
                    tick={({ x, y, payload }) => {
                      // Convert to title case
                      const titleCase = payload.value
                        .toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');

                      // Split into multiple lines if needed
                      const words = titleCase.split(' ');
                      const lines = [];
                      let currentLine = '';

                      words.forEach(word => {
                        const testLine = currentLine ? `${currentLine} ${word}` : word;
                        if (testLine.length > 20 && currentLine) {
                          lines.push(currentLine);
                          currentLine = word;
                        } else {
                          currentLine = testLine;
                        }
                      });
                      if (currentLine) lines.push(currentLine);

                      return (
                        <text x={x - 10} y={y} textAnchor="end" fill="#94a3b8" fontSize="0.65rem">
                          {lines.map((line, index) => (
                            <tspan key={index} x={x - 10} dy={index === 0 ? 0 : 12}>
                              {line}
                            </tspan>
                          ))}
                        </text>
                      );
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="charges"
                    fill="#dbeafe"
                    stroke="#2563eb"
                    strokeWidth={1}
                    radius={[0, 6, 6, 0]}
                    barSize={15}
                    name="Charges"
                  />
                  <Bar
                    dataKey="collections"
                    fill="#d1fae5"
                    stroke="#10b981"
                    strokeWidth={1}
                    radius={[0, 6, 6, 0]}
                    barSize={15}
                    name="Payments"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Charges & Collections by Location */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: '#1e293b',
                  fontSize: '1rem'
                }}
              >
                Charges & Payments by Location
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={revenueByLocation} layout="vertical" barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke="#94a3b8"
                    style={{ fontSize: '0.75rem' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatCurrency}
                  />
                  <YAxis
                    dataKey="location"
                    type="category"
                    stroke="#94a3b8"
                    width={120}
                    style={{ fontSize: '0.65rem' }}
                    axisLine={false}
                    tickLine={false}
                    tick={({ x, y, payload }) => {
                      // Convert to title case
                      const titleCase = payload.value
                        .toLowerCase()
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');

                      // Split into multiple lines if needed
                      const words = titleCase.split(' ');
                      const lines = [];
                      let currentLine = '';

                      words.forEach(word => {
                        const testLine = currentLine ? `${currentLine} ${word}` : word;
                        if (testLine.length > 20 && currentLine) {
                          lines.push(currentLine);
                          currentLine = word;
                        } else {
                          currentLine = testLine;
                        }
                      });
                      if (currentLine) lines.push(currentLine);

                      return (
                        <text x={x - 10} y={y} textAnchor="end" fill="#94a3b8" fontSize="0.65rem">
                          {lines.map((line, index) => (
                            <tspan key={index} x={x - 10} dy={index === 0 ? 0 : 12}>
                              {line}
                            </tspan>
                          ))}
                        </text>
                      );
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                  <Bar
                    dataKey="charges"
                    fill="#dbeafe"
                    stroke="#2563eb"
                    strokeWidth={1}
                    radius={[0, 6, 6, 0]}
                    barSize={15}
                    name="Charges"
                  />
                  <Bar
                    dataKey="collections"
                    fill="#d1fae5"
                    stroke="#10b981"
                    strokeWidth={1}
                    radius={[0, 6, 6, 0]}
                    barSize={15}
                    name="Payments"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ExecutiveOverview;
